/**
 * PhaseRepresentative — datos + documentos del Representante Legal.
 *
 * Paridad con persona física: la subida de identificación / CSF /
 * comprobante de domicilio dispara OCR en segundo plano y los campos
 * del formulario se pre-llenan automáticamente. El usuario solo
 * confirma o corrige. Aislamiento por sujeto garantizado por el
 * filtro ``(draft_id, doc_type, subject_type, subject_draft_id)`` que
 * implementa el endpoint de extracción.
 *
 * Orden visual: dropzone arriba, formulario debajo. La fase no cambia
 * de orden — el wizard sigue siendo CompanyData → CompanyDocs → REP …
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import SubjectFormFields from "./SubjectFormFields";
import SubjectDocUploader from "./SubjectDocUploader";
import DocFreshnessWarnings from "./DocFreshnessWarnings";
import useVigenciaRules from "./useVigenciaRules";
import useSubjectExtraction from "./useSubjectExtraction";
import mapExtractedToSubject from "./mapExtractedToSubject";
import PrefillPromptShell, { OcrUpsellHint } from "./PrefillPromptShell";

const REP_DRAFT_ID = "rep-main";

export default function PhaseRepresentative({
  data, ocrBySubject, onNext, onBack, loading, draftId,
}) {
  const repFromData = (data.legal_representatives || []).find(
    (r) => r.representation_type === "representante_legal",
  ) || {};
  const [profile, setProfile] = useState({
    representation_type: "representante_legal",
    ...repFromData,
  });
  const [warnKey, setWarnKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const { suffixFor, ocrEnabled } = useVigenciaRules();

  // ── Persisted OCR on phase re-entry. The `update_phase` endpoint
  // attaches a ``ocr_by_subject`` blob to the draft so when the user
  // comes back to this phase later they still see the pre-filled
  // values. Live polling (below) supersedes this in real time.
  useEffect(() => {
    const ocr = (ocrBySubject || {}).representative?.[REP_DRAFT_ID];
    if (!ocr) return;
    setProfile((prev) => mergeFill(prev, ocr));
  }, [ocrBySubject]);

  // ── Live OCR polling — paridad con PhaseDocuments del flujo PF.
  // El hook no hace nada si el plan no tiene OCR (no doom timeout).
  const extraction = useSubjectExtraction({
    draftId,
    subjectType: "representative",
    subjectDraftId: REP_DRAFT_ID,
    enabled: true,           // habilitado mientras el usuario está en la fase
    ocrEnabled,
    refreshKey,
  });

  useEffect(() => {
    if (!extraction.allDone) return;
    const mapped = mapExtractedToSubject(extraction.mergedData);
    if (Object.keys(mapped).length === 0) return;
    setProfile((prev) => mergeFill(prev, mapped));
  }, [extraction.allDone, extraction.mergedData]);

  const valid = (profile.full_name || "").trim() && (profile.curp || "").trim();
  const continueBlocked = !valid || loading || extraction.isExtracting;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Representante Legal</h2>
        <p className="text-sm text-slate-400">
          {ocrEnabled
            ? "Sube primero los documentos del representante; sus datos se autocompletarán."
            : "Captura los datos del representante y sus documentos."}
        </p>
      </div>

      {/* ── PRIMARY route: prominent prefill card (Pro) ──────────── */}
      {ocrEnabled && (
        <PrefillPromptShell
          testIdPrefix="rep-prefill"
          state={shellStateFromExtraction(extraction)}
          elapsedSec={extraction.elapsedSec}
          title="Sube los documentos del representante y autocompletamos sus datos"
          subtitle="Identificación oficial, CSF y comprobante de domicilio. Nosotros leemos nombre, CURP, RFC, vigencia y domicilio."
          collapseOnDone={false}
        >
          <SubjectDocUploader draftId={draftId} subjectType="representative"
            subjectDraftId={REP_DRAFT_ID}
            onUploaded={() => { setWarnKey((k) => k + 1); setRefreshKey((k) => k + 1); }}
            docs={[
              { docType: "rep_identificacion", label: "Identificación oficial", required: true },
              { docType: "rep_csf",             label: `CSF${suffixFor("rep_csf")}` },
              { docType: "rep_comprobante_domicilio", label: `Comprobante de domicilio${suffixFor("rep_comprobante_domicilio")}` },
              { docType: "rep_curp_pdf",        label: "CURP (PDF)" },
            ]}
          />
        </PrefillPromptShell>
      )}
      {!ocrEnabled && (
        <>
          <OcrUpsellHint
            testId="rep-ocr-disabled-hint"
            text="El pre-llenado automático desde la identificación / CSF / comprobante está disponible en"
          />
          <div className="space-y-3">
            <SubjectDocUploader draftId={draftId} subjectType="representative"
              subjectDraftId={REP_DRAFT_ID}
              onUploaded={() => { setWarnKey((k) => k + 1); setRefreshKey((k) => k + 1); }}
              docs={[
                { docType: "rep_identificacion", label: "Identificación oficial", required: true },
                { docType: "rep_csf",             label: `CSF${suffixFor("rep_csf")}` },
                { docType: "rep_comprobante_domicilio", label: `Comprobante de domicilio${suffixFor("rep_comprobante_domicilio")}` },
                { docType: "rep_curp_pdf",        label: "CURP (PDF)" },
              ]}
            />
          </div>
        </>
      )}
      <DocFreshnessWarnings
        draftId={draftId}
        subjectType="representative"
        subjectId={REP_DRAFT_ID}
        refreshKey={warnKey}
        testIdSuffix="representative"
      />

      {/* ── Formulario abajo ───────────────────────────────────────────── */}
      <SubjectFormFields
        profile={profile}
        onChange={setProfile}
        subjectKey="rep"
        includeContact
        includeResidence
        includePEP
        includeIdExpiry
      />

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>← Atrás</Button>
        <Button data-testid="rep-continue-btn" disabled={continueBlocked}
                onClick={() => onNext({
                  legal_representatives: [
                    ...(data.legal_representatives || []).filter(
                      (r) => r.representation_type !== "representante_legal",
                    ),
                    { ...profile, _draft_subject_id: REP_DRAFT_ID },
                  ],
                })}>
          {extraction.isExtracting
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Esperando OCR…</>
            : loading ? "Guardando…" : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}

/** Map a useSubjectExtraction return value to a PrefillPromptShell state. */
export function shellStateFromExtraction(extraction) {
  if (!extraction) return "idle";
  if (extraction.isExtracting) return "extracting";
  if (extraction.anyFailed) return "failed";
  if (extraction.allDone && Object.keys(extraction.mergedData || {}).length > 0) {
    return "done";
  }
  return "idle";
}

/** Merge OCR fields into a subject profile WITHOUT overwriting user edits. */
function mergeFill(prev, fields) {
  const next = { ...prev };
  for (const [k, v] of Object.entries(fields)) {
    if (!v) continue;
    if (prev[k] === undefined || prev[k] === "" || prev[k] === null) {
      next[k] = v;
    }
  }
  return next;
}
